// src/features/agent-builder/components/AgentBuilderStepper.tsx

import React from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { Check } from 'lucide-react';

interface Step {
  id: string;
  nameKey: string;
}

interface AgentBuilderStepperProps {
  steps: Step[];
  currentStepIndex: number;
  onStepClick: (stepIndex: number) => void;
  className?: string;
}

export const AgentBuilderStepper: React.FC<AgentBuilderStepperProps> = ({
  steps,
  currentStepIndex,
  onStepClick,
  className,
}) => {
  const { t } = useTranslation('agentBuilder');

  return (
    <nav aria-label="Agent Build Progress" className={clsx('bg-white p-2 sm:p-4 rounded-xl border-2 border-black', className)}>
      <ol role="list" className="flex items-center">
        {steps.map((step, stepIdx) => {
          const isCompleted = stepIdx < currentStepIndex;
          const isCurrent = stepIdx === currentStepIndex;
          const isClickable = isCompleted;

          return (
            <React.Fragment key={step.id}>
              <li className={clsx('relative md:flex-1', isClickable && 'group')}>
                <button
                  onClick={() => isClickable && onStepClick(stepIdx)}
                  disabled={!isClickable}
                  className={clsx(
                    'flex w-full items-center',
                    !isClickable ? 'cursor-default' : 'cursor-pointer'
                  )}
                  aria-current={isCurrent ? 'step' : undefined}
                >
                  <span className="flex items-center px-2 py-2 sm:px-6 sm:py-4 text-sm font-medium">
                    {/* Container for the icon and its shadow */}
                    <span className="relative">
                      {/* --- SHADOW ELEMENT (for "lift" effect) --- */}
                      {isClickable && (
                        <span
                          aria-hidden="true"
                          className="absolute inset-0 bg-black rounded-full transition-transform duration-150 ease-out group-hover:translate-x-px group-hover:translate-y-px"
                        />
                      )}
                      {/* --- VISIBLE ICON ELEMENT --- */}
                      <span
                        className={clsx(
                          'relative flex h-10 w-10 shrink-0 items-center justify-center rounded-full border-2',
                          isCompleted
                            ? 'border-emerald-600 bg-emerald-600 text-white'
                            : isCurrent
                            ? 'border-indigo-600 bg-white'
                            : 'border-slate-300 bg-white',
                          isClickable &&
                            'transition-transform duration-150 ease-out group-hover:-translate-x-px group-hover:-translate-y-px'
                        )}
                      >
                        {isCompleted ? (
                          <Check className="h-6 w-6" />
                        ) : (
                          <span className={clsx('font-bold', isCurrent ? 'text-indigo-600' : 'text-slate-500')}>
                            0{stepIdx + 1}
                          </span>
                        )}
                      </span>
                    </span>

                    <span
                      className={clsx(
                        'ml-4 hidden text-sm font-semibold md:block',
                        isCurrent ? 'text-indigo-600' : 'text-slate-700',
                        isClickable && 'group-hover:text-indigo-600'
                      )}
                    >
                      {t(step.nameKey)}
                    </span>
                  </span>
                </button>
              </li>
              {stepIdx !== steps.length - 1 ? (
                <div className="hidden h-0.5 w-full bg-slate-300 md:block" />
              ) : null}
            </React.Fragment>
          );
        })}
      </ol>
    </nav>
  );
};